/* ──────────────────────────────────────────────────────────────────────────
   Re-Download, the parts the popup and the server both need: the sorts, the
   filters a run is asked for, and its limits (Jay, 2026-10-21).
   ────────────────────────────────────────────────────────────────────────── */

export const SORTS = [
  { id: "views", label: "Most viewed" },
  { id: "recent", label: "Most recent" },
  { id: "multiplier", label: "Multiplier" },
] as const;

export type Sort = (typeof SORTS)[number]["id"];

/**
 * Multiplier takes only Reels at this many times their account's usual views or more (Ben, 2026-10-27:
 * "download me all videos that are above 1.5 multiplier"). With a count it is still a cap: ask for 20,
 * get the 13 that qualify. It is the line Research marks an outlier at (OUTLIER_AT); a test keeps them equal.
 */
export const MIN_MULTIPLIER = 1.5;

/**
 * What a run takes: from the accounts picked, any few of the creator's, or all
 * of them (null; Ben, 2026-10-31: "it doesnt let u do lets say 3 accounts"),
 * the first `count` Reels or every one (null), posted between two Melbourne
 * days, both included, or at any time (null), in the order asked.
 */
export type Filters = { accountIds: string[] | null; count: number | null; start: string | null; end: string | null; sort: Sort; ascending: boolean };

export const DEFAULT_COUNT = 30;
/** Copies of each Reel and whether they are adjusted, unless changed (Jay, 2026-10-21). The cap is manual duplication's. */
export const DEFAULT_DUPLICATES = 5;
export const MAX_DUPLICATES = 50;
/** The most Reels one run takes, the same cap as a manual duplication's per kind. */
export const MAX_REELS = 500;

export type RunState = "reading" | "downloading" | "duplicating" | "done" | "failed";

/** A creator's last run, as the popup shows it. */
export type RunView = {
  id: string;
  state: RunState;
  requestedAt: string;
  /** The Drive folder the copies went to, linked from the finished run's window. */
  destId: string;
  destName: string;
  /** What was asked for, so the window can say what was done. Handles in the creator's own order. */
  accounts: string[];
  filters: Filters;
  duplicates: number;
  adjust: boolean;
  picked: number;
  sent: number;
  failed: number;
  /** Engine tasks still to finish, and those stuck, while it is duplicating. */
  left: number;
  stuck: number;
  pages: number;
  error: string | null;
};

/**
 * What the Re-Download button on a creator's row shows of their latest run (Ben, 2026-10-31): in progress
 * while it runs, then done or failed until someone ticks it as checked. A creator with no run, or whose
 * latest is ticked, has none and shows the plain button.
 */
export type RowRun = { id: string; state: "running" | "done" | "failed"; destName: string; error: string | null };

/** "30 Reels", "All Reels", for the popup's preview line. */
export const countLabel = (count: number | null) => (count === null ? "All Reels" : `${count.toLocaleString()} Reel${count === 1 ? "" : "s"}`);
